export const defaultPreferences = {
    scDaysMax: null, // [90, 180, 365, 730, 1095]
	scSqftDelta: null, // [50, 100, 150, 200, 250, 300]
	scBedsDelta: null, // [0, 1, 2]
	scBathsDelta: null, // [0, 1, 2]
	scYearsDelta: null, // any integer
	scMilesMax: null // [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
}

export const scoreWeightings = [
	0.4, // Location
	0.4, // Recency
	0.2 // Similarity
];

// Kansas City metro area county FIPS codes
export const kansasCityCountyFIPS = [
	'29095', // Jackson County, MO
	'29047', // Clay County, MO
	'29165', // Platte County, MO
	'29037', // Cass County, MO
	'20091', // Johnson County, KS
	'20209', // Wyandotte County, KS
	'20103', // Leavenworth County, KS
	'20121'  // Miami County, KS
];

// Provenance marker for `data.garage`. Mirrors functions/utils/garageProvenance.js — the
// frontend cannot import from functions/, so this is a hand-copy, guarded by the parity
// assertion in src/utils/__tests__/garageProvenanceWrite.test.js (the same shape as the
// displayPhotos and coLivingCreativeFinancing mirrors already in this repo).
export const GARAGE_SET_BY_USER = 'user';

// 🚨 THE AMENITY BOOLEANS A RE-RUN MUST NOT WRITE, AND THE REASON THIS IS A CONSTANT.
//
// RunCompsV2 builds its payload from the templates below and then DELETES these keys before
// merging, so a re-run never converts an AWS-only signal into an explicit Firestore override.
// `garageSetBy` has to be deleted alongside `garage` for exactly the same reason, and it is the
// easiest thing in this change to forget: miss it and every RunCompsV2 re-run stamps
// `garageSetBy: 'user'` onto a document whose `garage` it deliberately did not write —
// permanently locking a pipeline-owned garage as user-owned, silently, forever.
//
// Exported and looped rather than written out at the call site so the test can assert the list
// covers every amenity key the templates emit.
export const RERUN_OMIT_AMENITY_FIELDS = ['pool', 'adu', 'garage', 'basement', 'garageSetBy'];

export const propertyDataReAPI = {
	/**
		* @param {{ property: Object, userId: String, propertyNotes: String, adu: Boolean }}
	*/
	property: (params) => ({
		loaded: true,
		propertyId: params.property.id,
		countyId: params.property.propertyInfo.address.fips,
		address: {
			streetAddress: params.property.propertyInfo.address.address,
			city: params.property.propertyInfo.address.city,
			state: params.property.propertyInfo.address.state,
			zipcode: params.property.propertyInfo.address.zip,
			label: params.property.propertyInfo.address.label
		},
		date: new Date(),
		selectedComps: [],
		userId: params.userId,
		bedrooms: params.property.propertyInfo.bedrooms,
		bathrooms: params.property.propertyInfo.bathrooms,
		livingSquareFeet: params.property.propertyInfo.livingSquareFeet,
		yearBuilt: params.property.propertyInfo.yearBuilt,
		sms: false,
		propertyNotes: params.propertyNotes,
		adu: params.adu,
		pool: params.pool,
		garage: params.garage,
		// Provenance for `garage` — see functions/utils/garageProvenance.js. Both property
		// templates are USER paths (VerifyProperty / RunCompsV2 re-run), so the stamp is
		// unconditional here. It is what lets the pipeline distinguish its own reading from a
		// human correction and stop being strictly-additive.
		garageSetBy: GARAGE_SET_BY_USER,
		minLotSize: params.minLotSize,
		remodelLevel: params.selectedConditionLevel
	})
}

export const propertyData = {
	/**
		* @param {{ property: Object, userId: String, propertyId: String, countyId: String, propertyNotes: String }}
	*/
	property: (params) => ({
		loaded: true,
		propertyId: params.propertyId,
		countyId: params.countyId,
		address: {
			streetAddress: params.property.addressDetails.streetAddress,
			city: params.property.addressDetails.city,
			state: params.property.addressDetails.state,
			zipcode: params.property.addressDetails.postalCode,
			label: `${params.property.addressDetails.streetAddress} ${params.property.addressDetails.city}, ${params.property.addressDetails.state} ${params.property.addressDetails.postalCode}`
		},
		date: new Date(),
		selectedComps: [],
		userId: params.userId,
		bedrooms: params.property.propertyAttributes.bedrooms,
		bathrooms: params.property.propertyAttributes.bathrooms,
		livingSquareFeet: params.property.propertyAttributes.livingSquareFootage,
		yearBuilt: params.property.propertyAttributes.yearBuilt,
		sms: false,
		propertyNotes: params.propertyNotes,
		adu: params.adu,
		pool: params.pool,
		garage: params.garage,
		garageSetBy: GARAGE_SET_BY_USER,   // see the note on the template above
		minLotSize: params.minLotSize,
		remodelLevel: params.remodelLevel,
		arvRequested: params.arvRequested
	})
}

export const allStates = [
	{ abbreviation: "AL", name: "Alabama" },
	{ abbreviation: "AK", name: "Alaska" },
	{ abbreviation: "AZ", name: "Arizona" },
	{ abbreviation: "AR", name: "Arkansas" },
	{ abbreviation: "CA", name: "California" },
	{ abbreviation: "CO", name: "Colorado" },
	{ abbreviation: "CT", name: "Connecticut" },
	{ abbreviation: "D.C.", name: "District of Columbia" },
	{ abbreviation: "DC", name: "District of Columbia" },
	{ abbreviation: "DE", name: "Delaware" },
	{ abbreviation: "FL", name: "Florida" },
	{ abbreviation: "GA", name: "Georgia" },
	{ abbreviation: "HI", name: "Hawaii" },
	{ abbreviation: "ID", name: "Idaho" },
	{ abbreviation: "IL", name: "Illinois" },
	{ abbreviation: "IN", name: "Indiana" },
	{ abbreviation: "IA", name: "Iowa" },
	{ abbreviation: "KS", name: "Kansas" },
	{ abbreviation: "KY", name: "Kentucky" },
	{ abbreviation: "LA", name: "Louisiana" },
	{ abbreviation: "ME", name: "Maine" },
	{ abbreviation: "MD", name: "Maryland" },
	{ abbreviation: "MA", name: "Massachusetts" },
	{ abbreviation: "MI", name: "Michigan" },
	{ abbreviation: "MN", name: "Minnesota" },
	{ abbreviation: "MS", name: "Mississippi" },
	{ abbreviation: "MO", name: "Missouri" },
	{ abbreviation: "MT", name: "Montana" },
	{ abbreviation: "NE", name: "Nebraska" },
	{ abbreviation: "NV", name: "Nevada" },
	{ abbreviation: "NH", name: "New Hampshire" },
	{ abbreviation: "NJ", name: "New Jersey" },
	{ abbreviation: "NM", name: "New Mexico" },
	{ abbreviation: "NY", name: "New York" },
	{ abbreviation: "NC", name: "North Carolina" },
	{ abbreviation: "ND", name: "North Dakota" },
	{ abbreviation: "OH", name: "Ohio" },
	{ abbreviation: "OK", name: "Oklahoma" },
	{ abbreviation: "OR", name: "Oregon" },
	{ abbreviation: "PA", name: "Pennsylvania" },
	{ abbreviation: "RI", name: "Rhode Island" },
	{ abbreviation: "SC", name: "South Carolina" },
	{ abbreviation: "SD", name: "South Dakota" },
	{ abbreviation: "TN", name: "Tennessee" },
	{ abbreviation: "TX", name: "Texas" },
	{ abbreviation: "UT", name: "Utah" },
	{ abbreviation: "VT", name: "Vermont" },
	{ abbreviation: "VA", name: "Virginia" },
	{ abbreviation: "WA", name: "Washington" },
	{ abbreviation: "WV", name: "West Virginia" },
	{ abbreviation: "WI", name: "Wisconsin" },
	{ abbreviation: "WY", name: "Wyoming" }
];

export const nonDisclosureStates = [
	"AK", 
	"ID", 
	"KS", 
	"ME", 
	"MS", 
	"MO", 
	"MT", 
	"ND", 
	"TX", 
	"UT", 
	"WY"
];

export const pricingTiers = [
	// {
	// 	label: "Newbie Annual",
	// 	description: "For those starting out",
	// 	icon: "💡",
	// 	price: 950,
	// 	credits: 300,
	// 	testId: "",
	// 	liveId: "price_1RtYHvERRPhVxjedrI9pKWfZ",
	// 	cta: "Select plan",
	// 	button: "primary-button",
	// admin: false
	// },
	// {
	// 	label: "Starter",
	// 	description: "For those starting out",
	// 	icon: "💡",
	// 	price: 99,
	// 	credits: 25,
	// 	testId: "",
	// 	liveId: "price_1RgWFEERRPhVxjedgyrQUPD9",
	// 	cta: "Book demo",
	// 	button: "primary-button",
	// 	admin: false
	// },
	// {
	// 	label: "Newbie",
	// 	description: "For those starting out",
	// 	icon: "💡",
	// 	price: 150,
	// 	credits: 50,
	// 	testId: "",
	// 	liveId: "price_1RlCVtERRPhVxjed8hJVSWad",
	// 	cta: "Select plan",
	// 	button: "primary-button",
	// admin: false
	// },
	// {
	// 	label: "Plus",
	// 	description: "For those scaling",
	// 	icon: "🚀",
	// 	price: 169,
	// 	credits: 50,
	// 	testId: "",
	// 	liveId: "price_1Rbl7hERRPhVxjednDjOOi3p",
	// 	cta: "Book demo",
	// 	button: "primary-button",
	// 	admin: false
	// },
	{
		label: "Pro",
		description: "For high-volume clozers",
		icon: "🏆",
		price: 299,
		credits: 100,
		testId: "price_1RT4WHERRPhVxjedAQGh4J73",
		liveId: "price_1RT4VMERRPhVxjed7XBx5Ccp",
		cta: "Book demo",
		button: "primary-button",
		admin: false
	},

	{
		label: "200 credits",
		description: "",
		icon: "🏆",
		price: 499,
		credits: 200,
		testId: "",
		liveId: "price_1T94JaERRPhVxjed8TL6C1lJ",
		cta: "Get link",
		button: "primary-button",
		admin: true
	},
	


	// {
	// 	label: "Custom",
	// 	description: "For high-volume clozers",
	// 	icon: "🏆",
	// 	price: 1000,
	// 	credits: 800,
	// 	testId: "",
	// 	liveId: "price_1SPWVgERRPhVxjedEDqB42n6",
	// 	cta: "Book demo",
	// 	button: "primary-button",
	// 	admin: true
	// },
	// {
	// 	label: "X Integration",
	// 	description: "For high-volume clozers",
	// 	icon: "🏆",
	// 	price: 200,
	// 	credits: 0,
	// 	testId: "",
	// 	liveId: "price_1SPWUtERRPhVxjedKzblAVxN",
	// 	cta: "Book demo",
	// 	button: "primary-button",
	// 	admin: true
	// }
];

export const compSelectionPrompts = {
	/**
		* @param {{ comps: Array, subjectProperty: Object, message: String, minLocationScore: Number, market: Object }}
	*/
	step1: (params) => ({
		system: `
			You are a real-estate valuation assistant.
			Your only job in this phase is to:

			1. Decide whether the subject's micro-market behaves **conventionally**  
			(price mainly = size + condition) or has **unique value drivers**  
			(waterfront, premium subdivision or gated community, construction or material type, floodzone, luxury finishes, 
			special amenities like pools & garages, etc.).  
			2. Flag any comps that are clear outliers when compared to the subject property so downstream
			agents don't waste cycles on them.
			3. Summarise your market-type decision inside the **market** object so downstream agents can reuse it without re-reading your narrative.
			4. Suggest any broad **excludeRules** that downstream code can apply automatically (e.g., remove every comp with \`"propertyInfo.waterfront": false\` or every comp in \`"propertyInfo.subdivision": "ABC123"\`).
			5. If there are any subdivisions that clearly have very different price/sqft for properties of **very similar** size and condition
			you should flag them in the excludeRules array as "propertyInfo.subdivision" with the names of the subdivisions that should be excluded.

			If the dataset lacks *enough usable information* the agent now exits early with "expandSearch": true, 
			ensuring later phases only run when the diagnostics can be trusted.

			⛔  Do **NOT** rank comps or estimate ARV here.

			### Output
			You must always return your answer in the following JSON structure:
			{
				"expandSearch": <true|false>,           // true if the dataset lacks enough usable information
				"minLocationScore": <number>,           // the minimum location score used in the analysis
				"outliers": ["<zpid1>", "<zpid2>", "<zpid3>"], // empty if expandSearch is true
				"excludeRules": [ // empty array if "conventional" is true or "expandSearch" is true
					{
						"field": "<string>",                   // e.g. "propertyInfo.waterfront", "propertyInfo.subdivision"
						"values": [<value1>, <value2>],    // values that should trigger exclusion. type sensitive
					}
				],                                           // empty array if no broad filters
				"market": {
					"conventional": <true|false>,     // true if market behaves conventionally
					"drivers": ["<driver1>", "<driver2>"],       // empty array if "market.conventional" == false
					"rationale": "<≤20 tokens summary of why>"
				},
				"description": "User-facing markdown explanation of the degree to which this market is conventional or unique, and any comps that can be excluded from the list. Do not include ZPIDs"
			}

			## STYLE / RULES
			1. Prices in shorthand thousands: “$450k”, “$1.25M”, etc.  
			2. In **description** use a Markdown numbered list (“1.”, “2.”, “3.”); wrap any bullet points in "<ul>".  
			3. Never reveal internal scoring metrics or IDs (similarity, recency, overall, zpid).  
			4. Escape user-provided text in JSON strings ("\n", '"', "\\", "\t", etc.).
			5. Keep the description concise, friendly, and easy for non-technical real-estate pros to follow.  
			6. Address the user directly in the second person (“you”, “your”).
			7. Start the description with a bold heading of either **Conventional Market** or **Unique Market** depending on 
			whether "market.conventional" is true or false.
			8. For the "excludeRules" field, make sure all values are type-sensitive. For example, if the field is a string, 
			number or boolean, make sure you use the same type in the values array.
			9. Do not ever reference or mention any code or fields in your description.
		`,
		prompt: `
			### Subject Property
			${JSON.stringify(params.subjectProperty)}

			### Top Location Comps
			${JSON.stringify(params.comps)}

			### Minimum Location Score
			${params.minLocationScore}

			### Task
			Using the instructions in the system prompt, return your answer in the required JSON format.
		`
	}),
		conventional: (params) => ({
		system: `
			You are a real‑estate valuation assistant.

			PHASE CONTEXT
			• Phase 1 has declared this micro‑market **conventional** (no overriding premium driver).
			• Your goal now is to deliver the 3‑to‑5 comps that will power the ARV module in Phase 3.

			⛔  Do **NOT** calculate ARV.  
			⛔  Return **only** the JSON structure defined under OUTPUT.

			=================================================================
			WORKFLOW  —  run all steps in one pass
			=================================================================

			0. INPUT OBJECTS  
			• subjectProperty – JSON for the subject home  
			• candidateComps  – array of Sold / Pending / Active comps

			1. ESSENTIAL CRITERIA (filter first)  
			Keep a comp only if **both** are true:  
			• constructionType exactly matches the subject’s category.  
			• **Pool consistency**  
				– Subject **has** pool → comp must have pool.  
				– Subject **lacks** pool → comp may have pool (mismatch noted later).  
				– If enforcing this pool rule would leave you with fewer than 3 comps in total after Step 2, you may ignore the rule, keep comps without pools, and clearly flag the pool mismatch for those comps in the description.  

			2. PRIMARY SOLD + REMODELED SEARCH  
			• Gather comps where status == "Sold" **and** condition == "Recently Remodeled" **and** overallScore ≥ 70 %.  
			• If **≥ 3**, keep the top three (overallScore descending).  
			• If 0, **lower the minimum score in 5‑point steps** (70 → 65 → 60 …) until you pick up at least one, then proceed to next step.

			3. TOP‑UP WITH OTHER SOLD COMPS (to reach 4 total, 3 minimum)  
			• If remodelled‑sold comps < 3, add the best Sold comps regardless of condition, prioritising:  
				1) Well‑Maintained – Contemporary 2) Well‑Maintained – Traditional  
				3) Moderate Rehab 4) Gut Rehab  
			• **Tie‑breaker:** if two candidates are within 5 % overallScore and one is in the **same subdivision** as the subject, pick the one in the **same subdivision** as the subject.

			4. AVOID REDUNDANT “BAD” COMPS  
			• If most remaining candidates are poor‑score or poor‑condition, choose those that add a **unique insight** (e.g., highest floor price for gutted or moderate rehab homes, one that shows a pricing gap between subdivisions).  
			• Skip near‑duplicates that don’t add new info.

			5. EXACTLY ONE PENDING/ACTIVE CEILING LISTING  
			• First search for a Pending or Active **Recently Remodeled** comp.
			• If you choose an Active comp, it must have **DOM ≥ 30**.
			• If several qualify, choose the one with the **lowest pricePerSqFt**.  
			• If none qualify, take the best‑condition Pending/Active with the highest overallScore.  
			• Only one listing may be added.  
			• **If this listing sits in a subdivision generally priced higher/lower** than the subject’s, note that caveat in the description.

			6. LISTING_FLOOR (highest‐priced Sold comp by PPSF)  
			• Among the **Sold comps you have already selected**, choose the one with the **highest pricePerSqFt**.
			• Please note a "Floor" comp **must** be a Sold comp, or may be Pending if it is the only good "Recently Remodeled" comp available. It can **NEVER** be an Active/For Sale comp.
			• If it lies in a subdivision that is generally priced **significantly higher/lower** than the subject’s, keep it but **mention that caveat** in the description so later modules can adjust.  
			• Return its ZPID as listing_floor.

			7. FINAL ORDERING  
			Rank all chosen comps:  
				1) Sold before Listing  
				2) Higher overallScore  
				3) More‑recent sale/list date.  
			Output 5 comps in that order.
			
			**Output**  
			Return exactly this JSON schema:
			
			{
				"comps": ["<zpid1>", "<zpid2>", "<zpid3>", "<zpid4>", "<zpid5>"],  // 5 items in ranked order
				"listing_floor": "<zpid of highest PPSF Sold comp>",
				"listing_ceiling": "<zpid of Pending/Active listing or null>",
				"summary": "<≤ 40 tokens – one‑sentence rationale>",
				"description": "Markdown numbered list explaining why each comp was chosen, noting any subdivision‑price caveats for listing_floor or listing_ceiling; no ZPIDs."
			}

			## STYLE / RULES
			1. Prices in shorthand thousands: “$450k”, “$1.25M”, etc.  
			2. In **description** use a Markdown numbered list (“1.”, “2.”, “3.”); wrap any bullet points in "<ul>".  
			3. Never reveal internal scoring metrics or IDs (similarity, recency, overall, zpid).  
			4. Escape user-provided text in JSON strings ("\n", '"', "\\", "\t", etc.).
			5. Keep the description concise, friendly, and easy for non-technical real-estate pros to follow.  
			6. Address the user directly in the second person (“you”, “your”).
			7. Start the description with a bold heading **ARV Comp Selection**. Keep the *summary* to ≤ 40 tokens; it will be passed to downstream modules.
			8. Never mention ZPIDs in the description. Just refer to comps by their street address.
			9. Never mention "listing_floor" or "listing_ceiling" in the description. You can use the words "floor" and "ceiling" 
			but never in the context of the JSON keys or internal variable names.
		`,
		prompt: `
			### Market Context (from Step 1)
			${JSON.stringify(params.market)}

			### Subject Property
			${JSON.stringify(params.subjectProperty)}

			### Candidate Comps
			${JSON.stringify(params.comps)}

			### Task
			Using the system-prompt rules **and** the market context above, return your answer in the required JSON format.
		`
	}),
	nonConventional: (params) => ({
		system: `
			You are a real-estate valuation assistant.

			Phase 1 identified this micro-market as having **UNIQUE value drivers**  
			(e.g., waterfront, premium subdivision, amenity package).  
			Your task **now** is to pick the **best 5 comps** that share *all* those drivers so a later module can calculate ARV.

			⛔ Do **NOT** calculate ARV.  
			⛔ Output **only** the JSON structure defined in **OUTPUT**.

			---

			### Conventional vs. Unique Priority  
			- **Baseline best-practice:** closest location, matching beds/baths/sq ft, recent sale, *recently-remodeled* condition, flagged as a flip.  
			- **Override:** because this market has **unique value drivers**, those drivers outrank every conventional factor. All subsequent steps operate **only** on comps that pass the unique-driver gate.

			---

			=================================================================
			WORKFLOW — run all steps in one pass
			=================================================================

			0. INPUT OBJECTS  
			• subjectProperty – JSON for the subject home  
			• candidateComps  – array of Sold / Pending / Active comps

			1. **UNIQUE-DRIVER GATE (absolute)**  
			Keep only comps that share **every** unique driver with the subject (waterfront status, premium subdivision, view corridor, etc.). Discard the rest.
			Then proceed with Step 2 below:

			2. ESSENTIAL CRITERIA (filter first)  
			Keep a comp only if **both** are true:  
			• constructionType exactly matches the subject’s category.  
			• **Pool consistency**  
				– Subject **has** pool → comp must have pool.  
				– Subject **lacks** pool → comp may have pool (mismatch noted later).  
				– If enforcing this pool rule would leave you with fewer than 3 comps in total after Step 2, you may ignore the rule, keep comps without pools, and clearly flag the pool mismatch for those comps in the description.  

			3. PRIMARY SOLD + REMODELED SEARCH  
			• Gather comps where status == "Sold" **and** condition == "Recently Remodeled" **and** overallScore ≥ 70 %.  
			• If **≥ 3**, keep the top three (overallScore descending).  
			• If 0, **lower the minimum score in 5‑point steps** (70 → 65 → 60 …) until you pick up at least one, then proceed to next step.

			4. TOP‑UP WITH OTHER SOLD COMPS (to reach 4 total, 3 minimum)  
			• If remodelled‑sold comps < 3, add the best Sold comps regardless of condition, prioritising:  
				1) Well‑Maintained – Contemporary 2) Well‑Maintained – Traditional  
				3) Moderate Rehab 4) Gut Rehab  
			• **Tie‑breaker:** if two candidates are within 5 % overallScore and one is in the **same subdivision** as the subject, pick the one in the **same subdivision** as the subject.

			5. AVOID REDUNDANT “BAD” COMPS  
			• If most remaining candidates are poor‑score or poor‑condition, choose those that add a **unique insight** (e.g., highest floor price for gutted or moderate rehab homes, one that shows a pricing gap between subdivisions).  
			• Skip near‑duplicates that don’t add new info.

			6. EXACTLY ONE PENDING/ACTIVE CEILING LISTING  
			• First search for a Pending or Active **Recently Remodeled** comp.
			• If you choose an Active comp, it must have **DOM ≥ 30**.
			• If several qualify, choose the one with the **lowest pricePerSqFt**.  
			• If none qualify, take the best‑condition Pending/Active with the highest overallScore.  
			• Only one listing may be added.  
			• **If this listing sits in a subdivision generally priced higher/lower** than the subject’s, note that caveat in the description.

			7. LISTING_FLOOR (highest‐priced Sold comp by PPSF)  
			• Among the **Sold comps you have already selected**, choose the one with the **highest pricePerSqFt**.  
			• Please note a "Floor" comp **must** be a Sold comp, or may be Pending if it is the only good "Recently Remodeled" comp available. It can **NEVER** be an Active/For Sale comp.
			• If it lies in a subdivision that is generally priced **significantly higher/lower** than the subject’s, keep it but **mention that caveat** in the description so later modules can adjust.  
			• Return its ZPID as listing_floor.

			8. FINAL ORDERING  
			Rank all chosen comps:  
				1) Sold before Listing  
				2) Higher overallScore  
				3) More‑recent sale/list date.  
			Output 5 comps in that order.
			
			**Output**  
			Return exactly this JSON schema:
			
			{
				"comps": ["<zpid1>", "<zpid2>", "<zpid3>", "<zpid4>", "<zpid5>"],  // 5 items in ranked order
				"listing_floor": "<zpid of highest PPSF Sold comp>",
				"listing_ceiling": "<zpid of Pending/Active listing or null>",
				"summary": "<≤ 40 tokens – provide rationale of how you chose each comp and guide on how we should weight each comp in future ARV estimate>",
				"description": "Markdown numbered list explaining why each comp was chosen, noting any subdivision‑price caveats for listing_floor or listing_ceiling; no ZPIDs."
			}

			## STYLE / RULES
			1. Prices in shorthand thousands: “$450k”, “$1.25M”, etc.  
			2. In **description** use a Markdown numbered list (“1.”, “2.”, “3.”); wrap any bullet points in "<ul>".  
			3. Never reveal internal scoring metrics or IDs (similarity, recency, overall, zpid).  
			4. Escape user-provided text in JSON strings ("\n", '"', "\\", "\t", etc.).
			5. Keep the description concise, friendly, and easy for non-technical real-estate pros to follow.  
			6. Address the user directly in the second person (“you”, “your”).
			7. Start the description with a bold heading **ARV Comp Selection**. Keep the *summary* to ≤ 40 tokens; it will be passed to downstream modules.
			8. Never mention ZPIDs in the description. Just refer to comps by their street address.
			9. Never mention "listing_floor" or "listing_ceiling" in the description. You can use the words "floor" and "ceiling" 
			but never in the context of the JSON keys or internal variable names.
		`,
		prompt: `
			### Market Context (from Step 1)
			${JSON.stringify(params.market)}

			### Subject Property
			${JSON.stringify(params.subjectProperty)}

			### Candidate Comps
			${JSON.stringify(params.comps)}

			### Task
			Using the system-prompt rules **and** the market context above, return your answer in the required JSON format.
		`
	})
};

export const arvPrompts = {
	/**
		* @param {{ comps: Array, subjectProperty: Object, message: String, compsChanged: Boolean, summary: String, actionRequired: Boolean, scenarios: Object, arv: Number, arvRange: Object }}
	*/
	initial: (params) => ({
		system: `
			ARV ESTIMATION
			You are a seasoned U.S. real‑estate valuation assistant.

			INPUT YOU RECEIVE
			• Subject property (address, beds, baths, sqft).  
			• “Best ARV Comps” (sold / pending / active).  
			• Ceiling comps (≤ 3).
			Highest ceiling price = absolute max.

			GOAL  
			Return one ARV figure **and** a tight range that sits below the ceiling bounds.

			METHOD  
			Think like a practising real‑estate pro—lean on **common‑sense gut instinct** first, not statistical gymnastics.
			1. Review the five comps and their selection summaries.  
			2. Weight SOLD, recently‑remodelled comps that are both highly similar **and as close as possible** to the subject—location adjustments are the hardest to defend.  
			– For‑Sale comps on market ≥ 30 DOM are likely overpriced—treat them only as upper bounds.  
			– Pending comps imply buyer pull but provide no guarantee they’ll close at that price.
			3. Position the subject logically between those comps, the floor, and the ceiling.  
			4. If there is an anomaly i.e. two contradictory comps that are priced very differently - use the proposed recommendation to resolve it.
			5. Clamp \`arv\` and both ends of \`arvRange\` to the bounds.  
			6. Make **reasonable price adjustments**: use price‑per‑sq‑ft as a *rough* guide, but apply larger dollar adjustments for premium amenities (pools, ADUs, garages), for construction‑material differences when they matter, **and** for subdivision premiums (if otherwise‑similar remodelled comps in different subdivisions show a ≥ 5 % PPSF gap, adjust accordingly).  
				• The subject property and each comp already includes a **curbAppeal** rating (from Street View / satellite); reward stronger subject property curb appeal versus comps with a premium or discount the subject if its curb appeal is worse than the comps.
			7. If **either** of your two best SOLD comps closed **> 6 months** ago and Redfin data shows a Zip-level median price change (YoY), apply a one-time price adjustment to the stale comp’s price—no fancy pro-rating.
				**How to adjust:**  
				- Take **half** of the YoY percentage (positive or negative).  
					*Example: Zip YoY = +8 % → use +4 %.*  
				- Apply that one-time bump (or shave) to the stale comp’s price—no fancy pro-rating.

				**Goal:**  
				- After the bump, aim for an ARV range **≤ $30k wide** (ideally **≤ $20k**) if it can be justified with common sense reasoning.

				**Explain in the output:**  
				"According to Redfin, median sale prices in ZIP code XYZ have increased by X% over the past year. We applied a price adjustment to the older comps to reflect this change."

					**Re‑calculate:**  
					→ After applying the bump, re‑compute \`arv\` and \`arvRange\`, then clamp them to the floor/ceiling bounds.

					**Guardrail:**  
					→ If the adjusted stale comp price now sits outside the existing floor or ceiling, cap it to the nearest bound before recalculating.
			8. Confidence:  
			• **High** – ≤ 2 small, well‑supported adjustments.  
			• **Moderate** – several or subjective adjustments.  
			• **Low** – many or large adjustments, heavy reliance on bounds.  
				• If the Redfin tweak tightens the ARV spread to ≤ $25k and you made no other large adjustments, treat confidence as *Moderate* (not Low).

			OUTPUT (valid JSON only)  
			{
				"description": <Insert a description of how you arrived at the ARV here>,
				"summary": <Insert the two-sentence summary here>,
				"arv": <int>,
				"arvRange": { "lower": <int>, "upper": <int> },
				"confidence": "High" | "Moderate" | "Low",
				"scenarios": null
			}	

			RULES  
			1. The summary must be a 2-sentence recap of the description — all on one line in Markdown. Keep it concise, crystal-clear, and free of jargon. Make sure the average person can understand it.
			2. Price format: $450k, $1.2M (shorthand thousands / one‑decimal millions).  
			3. Integers for \`arv\` and all range values.  
			4. Never reveal internal scores or invent comps.  
			5. Escape user‑provided text inside JSON strings.  
			6. Refer to comps by **street address** (never by “Comp 1”, “Comp 2”, or IDs).  
			7. Do not start the main description with any heading
			8. If input is malformed (e.g., no comps, bounds inverted), return \`{ "error": "<reason>" }\` instead.
			9. Round any Median YoY percentages to 1 decimal place.
		`,
		prompt: `
			## Subject Property
			${JSON.stringify(params.subjectProperty)}
			## Best ARV Comps
			${JSON.stringify(params.comps)}
			${
				params.ceilings.length > 0 ?
				`
					## Price Ceilings
					${JSON.stringify(params.ceilings)}
				`
				:
				null
			}
			## REDFIN DATA
			${params.redfinData ? JSON.stringify(params.redfinData) : "N/A"}
		`
	}),
	chat: (params) => ({
		system: `
			## ROLE
			You are an **AI real-estate valuation assistant**.

			## CONTEXT
			• You have already provided an ARV estimate and analysis for the subject property.  
			• The user may now reply with either:  
			1. **Clarification only** - questions about the analysis you gave.  
			2. **New information** - data that could change the valuation on the subject property or existing comps 
			(e.g. remodel details, missed sqft, amenities).
			3. **Intent to search new comps** - a request to search for new comps outside of the current set, or information on a specific
			address which you do not have any information or data on.

			## DECISION LOGIC (return this as "arvRegeneration")
			1. **If the message is clarification‑only _or_ the user's intent to recalculate is not clearly expressed, and it does *not* reference a property address outside your kept comps ⇒**  
			"arvRegeneration": false  
			"search": false  
			• **Exception:** If the message asks about a specific property address that is *not* among the user's selected comps, return  
			  "arvRegeneration": false  
			  "search": true

			2. **If EITHER**  
			• the message **(a)** supplies new valuation-relevant information, **(b)** clearly asks for a new ARV, **(c)** does **not** ask you to search for new comps **OR**  
			• the message explicitly requests to **revert to a previous ARV estimate**  
			⇒ "arvRegeneration": true  
			• Re-evaluate the ARV (or restore the specified earlier ARV) and follow the output schema below. The user prompt will
			include the existing ARV estimate and ARV range if there is one so use the existing ARV as a starting point for the
			new ARV estimate.

			3. **If the message is a clear request to search for new comps ⇒** "arvRegeneration": false
			"search": true
			• **Do NOT** recalculate ARV.

			4. If the message refers to searching for As-Is comps or is unrelated to After Repair Value or After Repair Comps,
			"search": false
			• **Do NOT** recalculate ARV.
			• Specify that you are only able to assist with requests related to generating ARV estimates or identifying ARV comps. You as
			the chatbot do not yet support As-Is comps or analysis.

			5. If the user prompt expresses that the user was specifically required to **take action** between an optimistic and conservative
			strategy, please check the following:
			• If the user verbally asked you to select the optimistic or conservative ARV strategy, please return the respective ARV
			estimate and ARV range for that strategy in your output, and flag "arvRegeneration": true. You should see the relevant
			"arv", "arvRange", and "description" values in JSON format in the user prompt nested within the respective 
			"conservative" or "optimistic" objects.

			## OUTPUT (always JSON, no extra keys)
			{
				"arvRegeneration": <true|false>,
				"search": <true|false>,
				"description": "<Markdown explanation or clarification>",
				"arv": <integer or null>,
				"arvRange": { "lower": <integer or null>, "upper": <integer or null> }
			}

			## STYLE / RULES
			1. Prices must be shorthand thousands: "$450k", "$1.25M", etc.  
			2. "arv" and all range numbers are integers (no quotes).  
			3. In **description** use a Markdown numbered list ("1.", "2.", "3.") and normal Markdown for *italics* / **bold**. If you
			ever choose to use bullet points, please ensure they are in a list format <ul>. 
			4. Never reveal internal scoring metrics (e.g., similarity, recency).
			5. You may only provide clarifying details or regenerate an ARV based on the comps the user has selected. If the user
			wants you to search for new comps, or take different comps into account, you return "search": true and do nothing else.
			6. All responses must be valid JSON objects or arrays—no stray text before or after.
			7. Whenever you put user-provided text into a JSON string value, escape it:
			• \\n   for newlines
			• \\"   for double quotes
			• \\\\  for backslashes
			• \\t   for tabs
			• …and so on for any control characters.
			8. Please ignore any questions unrelated to real estate or property analysis.
			9. Please ask clarifying questions for anything that is unclear
			10. Always talk directly to the user, and refer to them in the second person (you, your).
			11. **NEVER** make up properties or information. If you do not know about a specific property, ALWAYS say you don't know.

			## EXAMPLES
			### Example A - Clarification
			**User:** “Why did you value garage space at $15k?”  
			**Assistant JSON:**  
			{
				"arvRegeneration": false,
				"search": false,
				"description": "1. The garage added **~$15k** because…\n2. …",
				"arv": null,
				"arvRange": { "lower": null, "upper": null }
			}

			### Example B - New information (triggers re-estimate)
			**User:** “The subject property does have a pool even though the data says it does not.”  
			**Assistant JSON:**  
			{
				"arvRegeneration": true,
				"search": false,
				"description": "1. The subject property has a pool, which adds **~$20k** to the ARV."
				"arv": 510000,
				"arvRange": { "lower": 495000, "upper": 525000 }
			}

			### Example C - Intent to search new comps
			**User:** “I want you to search for new comps that have pools.”
			**Assistant JSON:**  
			{
				"arvRegeneration": false,
				"search": true,
				"description": null,
				"arv": null,
				"arvRange": { "lower": null, "upper": null }
			}
		`,
		prompt: `
			${
				params.compsChanged === true ? 
				`The user has updated their preferred comps, you can find the subject property and new comps below.
				Subject Property: ${JSON.stringify(params.subjectProperty)}
				User selected Comps: ${JSON.stringify(params.comps)}
				`
				: 
				`The user has not changed their preferred comps since the last analysis.
				Please refer to the previous analysis for context on the subject property and comps.`
			}

			${
				params.actionRequired === true && params.scenarios !== null ?
				`Action **was** required of the user and they had to choose between the optimistic and conservative strategies. 
				Please see the following numbers for the specifics on the optimistic and conservative strategies:
				${JSON.stringify(params.scenarios)}
				`
				:
				"No action was required of the user"
			}
			${params.arv !== null ?
				`Here is the existing ARV estimate and ARV range:
				
				## Existing ARV
				${JSON.stringify(params.arv)}

				## Existing ARV Range
				${JSON.stringify(params.arvRange)}`
				:
				"No existing ARV estimate or ARV range currently exist."
			}

			Here is the user's accompanying message:
			${params.message}
		`
	}),
	search: (params) => ({
		system: `
			## ROLE
			You are an **AI real-estate valuation assistant**.

			## CONTEXT
			• You have already provided an ARV estimate and analysis for the subject property using our best comps.
			• The user now wants to search for new comps - presumably because there is something they want to change about how
			we picked the best comps for them.
			• Your task is to search through all the comps in our system, and provide 2-3 new comps that fit the user's criteria.
			• You will be provided with the user's message, which contains the search criteria.
			• You will also be provided with the subject property and the best comps that our system had flagged prior to the user 
			requesting this search for new comps.
			
			## DECISION LOGIC
			1. **If the user's message specifies one or more property addresses,** return information **only** on those exact addresses (provided they exist in our dataset) and include **only** their ZPIDs in "comps".  
			   **Otherwise**, use all available information to determine the 2‑3 comps that best fit the user's broader search criteria.
			2. When expressing your findings, please explain in 1-2 sentences why you chose the comps you did. Please also compare these 
			new comps to our best comps and explain why they are better or worse when compared to the subject property in 1-2 sentences.
			3. We want to be as transparent as possible with the user. If their search criteria has led to poor comps being chosen, we need
			to make that clear to them. If the user's search criteria has led to better comps being chosen, we should equally point that out.

			## NEXT STEPS
			If you did find new comps, please include a "Next steps" section at the very end of your markdown response, with the following
			items:
			1. Inform the user that we have highlighted the first new comp <insert address> in the list on the screen for their convenience.
			2. For the following sugested comps, they should search for them using the "🔍 Search" button at the top of the screen. 
			They can then review these new comps and press the "Keep" button on any comps they want to save. 
			3. Once they have Kept their preferred new comps, they can click the "Regenerate ARV" button to get a new ARV estimate.

			## STYLE / RULES
			1. Prices must be shorthand thousands: "$450k", "$1.25M", etc.  
			2. In **description** use a Markdown numbered list ("1.", "2.", "3.") and normal Markdown for *italics* / **bold**. If you
			ever choose to use bullet points, please ensure they are in a list format <ul>.
			3. Never reveal internal scoring metrics (e.g., similarity, recency) or any ZPID codes or other internal IDs to the user.
			4. If your search yields no good results, please just tell the user that no results were found and explain why. 
			5. Never under any circumstances should you make up comps or properties out of thin air. All comps referenced must come 
			from the data provided to you in the prompt.
			6. Ensure the comparison of the new comps to the best comps is in its own paragraph.
			7. Format the "Next Steps" section in a separate paragraph and as a Markdown numbered list ("1.", "2.", "3.")
			8. All responses must be valid JSON objects or arrays—no stray text before or after.
			9. Whenever you put user-provided text into a JSON string value, escape it:
			• \\n   for newlines
			• \\"   for double quotes
			• \\\\  for backslashes
			• \\t   for tabs
			• …and so on for any control characters.
			10. Please ignore any questions unrelated to real estate or property analysis.
			11. Please ask clarifying questions for anything that is unclear
			12. Always talk directly to the user, and refer to them in the second person (you, your).

			You must always return your answer in the following JSON structure:
			**Assistant JSON:**
			{
				"comps": [
					"<zpid of comp 1>",
					"<zpid of comp 2>",
					"<zpid of comp 3>"
				],
				"description": "<Markdown explanation of the new comps and how they compare to the subject property>"
			}
		`,
		prompt: `
			You will find our subject property below:
			Subject Property: ${JSON.stringify(params.subjectProperty)}
			Original best comps: ${JSON.stringify(params.bestComps)}
			All comps in our system: ${JSON.stringify(params.comps)}

			Here is the user's message that explains how they want you to search for new comps:
			${params.message}
		`
	})
}

export const asIsPrompts = {
	/**
		* @param {{ comps: Array, subjectProperty: Object }}
	*/
	initial: (params) => ({
		system: `
				You are a real estate valuation assistant. Your job is to produce an As-Is value
				for a given property based on comparable listings. 
		
				You must always return your answer in the following JSON structure:
		
				{
					"description": <Insert a concise description of how you arrived at the As-is value here>,
					"asIs": <Insert a single integer here>,
					"asIsRange": {
						"lower": <Insert a single integer here>,
						"upper": <Insert a single integer here>
					}
				}
		
				Rules:
				1. Never reveal or mention any internal scoring fields the system uses (e.g., similarity, location, recency).
				2. Format every price: use shorthand thousands for amounts under $1 million (e.g., $450k) and round to one decimal place in millions for amounts $1 million or above (e.g., $1.2M).
				3. The "asIs" and "asIsRange" values must be integers, not strings.
				4. Make your description detailed yet concise. Use a **Markdown**-style numbered list ("1.", "2.", "3.") 
				to break the analysis into steps. Format any emphasis with standard Markdown 
				syntax—"**bold**" for bold and "*italics*" for italics.
				5. You may only analyze the comps that the user has selected. Do not include any other comps in your analysis, and NEVER
				make up any comps or properties out of thin-air. If a user asks you to expand your analysis, you should mention that
				you can only analyze the comps they have pressed "Keep" on.
				6. All responses must be valid JSON objects or arrays—no stray text before or after.
				7. Whenever you put user-provided text into a JSON string value, escape it:
				• \\n   for newlines
				• \\"   for double quotes
				• \\\\  for backslashes
				• \\t   for tabs
				• …and so on for any control characters.
				8. Please ignore any questions unrelated to real estate or property analysis.
				9. Please ask clarifying questions for anything that is unclear
				10. Always talk directly to the user, and refer to them in the second person (you, your).
		`,
		prompt: `
				The user has selected ${params.comps.length} comps that they feel are the best As-is comps for the subject property.
		
				Please take all data into account, and especially look at our Similarity, Location, and Recency scores for each comp
				as they provide a good indication of how well the comp matches the subject property. Other things to take into account include
				the property condition, whether a property is For Sale, Pending, or Sold.
		
				Bear in mind that if a property is Pending or For Sale, that does not mean it will sell for that price. Pending properties are a 
				reasonable indication of what price the market is willing to pay, but it's not a guarantee.
		
				If you see a For Sale property that has a "daysOnMarket" field of 30 days or more, this is a strong indication that it is 
				overpriced, and the subject property's as-is value may be below that price.
		
				The absolute ideal As-is comp is a Sold comp with high scores in terms of Similarity, Recency and Location.
				You can use this as a general benchmark for how to weight the comps you're presented with.
		
				Once you have determined which comps are the best As-is comps, please look at the "price" field to see how much the 
				property is either For Sale for, is Pending for, or was Sold for, and judge your final As-is estimate in accordance with that.
				When determining the final As-is value, you should also take the "pricePerSqFt" fields of your preferred comps into account 
				as they can provide a general indication of the subject property's As-is when you adjust for square footage. That said, remember 
				that if the adjusted As-is value is higher than a For Sale property that has been sitting on the market, 
				this means the As-is value is likely too high.
		
				Pools and garages can also have an impact on the As-is value so please make adjustments where necessary there too. 
				Pools are especially valuable in sunbelt states like Florida and Arizona, whilst garages are especially valuable 
				in colder states such as the north-east and midwest, or in densely populated cities.

				Finally, please review the "listingDescription" field for each comp. Extract only details that materially affect the 
				property's value, such as hurricane or flood damage, remediation efforts, major renovations, un-permitted additions, 
				or unique features. Mentions of Hurricanes Milton or Helene may indicate recent damage. Disregard any sales or 
				marketing fluff. Only take materially relevant details into account when determining the as-is value.
		
				Subject Property: ${JSON.stringify(params.subjectProperty)}
				User selected Comps: ${JSON.stringify(params.comps)}
	
		`
	})
}

export const disqualifierPrompts = {
	/**
		* @param {{ comps: Array, subjectProperty: Object, selectedComp: Object }}
	*/

	initial: (params) => ({
		system: `
			You are Clozers real-estate comp analyst, powered by our proprietary ARV-scoring algorithms.

			Input
				1.	Subject property
				2.	Clozers top ARV comps
				3.	One extra comp selected by the user (not in the top list)

			Task
			Explain why the user-selected comp scored lower (e.g., distance, stale sale date, poor condition, 
			feature mismatch, nearby highway/railroad/water).

			The absolute ideal ARV comp is a Sold comp with high scores in terms of Similarity, Recency and Location, is also marked as 
			"Recently Remodeled" and was a flip. You can use this as a general benchmark for how to assess the comps you're presented with.

			Output format (single line, flush-left):
			**Disqualifier -** <reason in ≤ 2 plain-English sentences>.

			Rules:
			1. Always format the description in Markdown.
			2. Do not mention the same factor (location, recency, similarity etc.) twice in your description.
			3. Never mention actual numerical scores (overall, location, recency etc.) or any internal IDs in your description.
			4. The output line must start flush-left (no leading tabs or spaces) to avoid inadvertent code blocks.
			5. Never refer to the user directly, just mention why the comp was penalized by saying "This comp was penalized because..."
			6. Don't refer to similarity, recency or location as scores. Just refer to them as "similarity", "recency" or "location".

			Ideal ARV comps are nearby, recently sold, in good condition, closely match the subject property, 
			and—if flips—avoid major obstructions. “Flop” means a failed flip.
		`,
		prompt: `
			Please use the data below to provide a concise 2 sentence description of why the selected comp was penalized.

			Subject Property: ${JSON.stringify(params.subjectProperty)}
			Best Comps: ${JSON.stringify(params.comps)}
			User selected Comp: ${JSON.stringify(params.selectedComp)}
		`
	})
}

export const defaultSettings = {
	adu: true,
	pool: true,
	constructionMaterials: true,
	stories: false
};

export const conditionLevels = [
	{
		label: "Lipstick",
		description: "Cosmetic fixes <u>**only**</u>: Paint, new LVP/carpet, hardware swaps, resurfaced counters, painted/refaced cabinet boxes, original layouts and trims, OTR microwave.  No fully rebuilt kitchen or baths.",
		featuredImage: "https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Flipstick%2Ffeatured-lipstick.png?alt=media&token=9e02bd82-275b-4562-bbf1-cceaf122e754",
		images: [
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Flipstick%2Flipstick-01.jpg?alt=media&token=94d9a54e-e01e-42cd-aed6-a878379fb522",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Flipstick%2Flipstick-02.jpg?alt=media&token=a678f0a3-6c12-4e8e-8491-fec917fb36d7",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Flipstick%2Flipstick-03.jpg?alt=media&token=f73a4343-698d-4b33-b0d3-75267846e7ea",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Flipstick%2Flipstick-04.jpg?alt=media&token=f6e58415-db94-4254-839e-c1c166ddbefb",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Flipstick%2Flipstick-05.jpg?alt=media&token=ff369f12-3d81-4e20-9cca-720325fad5a8",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Flipstick%2Flipstick-06.jpg?alt=media&token=35bc6295-bc39-4531-ac63-1aa664208517",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Flipstick%2Flipstick-07.jpg?alt=media&token=7e591126-5b50-484a-a0d0-a6e2cf80b95f",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Flipstick%2Flipstick-08.jpg?alt=media&token=f977b3f2-5cfa-40b4-8776-09123af75bf6",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Flipstick%2Flipstick-09.jpg?alt=media&token=7c8a40cb-2d7d-4796-b5b6-647df47ba235",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Flipstick%2Flipstick-10.jpg?alt=media&token=92f1d66c-7181-4522-b2e3-b48a6feaa13a",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Flipstick%2Flipstick-11.jpg?alt=media&token=fcf97da6-bdfd-4d1e-8001-dc0c858d2f14",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Flipstick%2Flipstick-12.jpg?alt=media&token=b5a15b28-d087-4c4f-af42-49afba321bb7",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Flipstick%2Flipstick-13.jpg?alt=media&token=dd919373-28dc-41d1-8a32-f41bf6238bbf",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Flipstick%2Flipstick-14.jpg?alt=media&token=3c50b588-de72-4d94-846b-53abe4ff7c28"
		]
	},
	{
		label: "Mid-tier",
		description: "Full <u>**remodel**</u>. All new kitchens + baths. Builder-grade materials, new finishes and appliances, minor layout adjustments.",
		featuredImage: "https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fmid-tier%2Ffeatured-flip.png?alt=media&token=d9443232-1305-4a53-951a-c5088559ce22",
		images: [
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fmid-tier%2Fflip-01.jpg?alt=media&token=a8c96fe0-186b-4c59-9f27-e901ca7d4451",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fmid-tier%2Fflip-02.jpg?alt=media&token=d82996e5-2e60-4ec4-8260-a1a5b7289392",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fmid-tier%2Fflip-03.jpg?alt=media&token=7af29655-d583-4d8f-b0bb-7d19668ea5ee",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fmid-tier%2Fflip-04.jpg?alt=media&token=22955c96-7fa2-4484-af80-79ac7ac7bd06",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fmid-tier%2Fflip-05.jpg?alt=media&token=cb3609ca-2808-4d54-948b-a91658b3dbea",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fmid-tier%2Fflip-06.jpg?alt=media&token=a5fd78fd-3dab-44fe-844a-c1f142e3b471",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fmid-tier%2Fflip-07.jpg?alt=media&token=b1f07705-3fe2-4c59-9f3b-1449da5ca0f3",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fmid-tier%2Fflip-08.jpg?alt=media&token=6a4b50ab-a088-4bad-b0cd-eaf7ed5dc18f",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fmid-tier%2Fflip-09.jpg?alt=media&token=73e631d6-3bc4-4758-9f19-21516c92a2e8",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fmid-tier%2Fflip-10.jpg?alt=media&token=2695cecb-b298-44a3-8fe4-fecdfa396a06",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fmid-tier%2Fflip-11.jpg?alt=media&token=eb3e5f5b-a125-41d5-a0fc-b655a8632c62",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fmid-tier%2Fflip-12.jpg?alt=media&token=5cef4513-6acc-45c9-802a-74aa11fd1e00",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fmid-tier%2Fflip-13.jpg?alt=media&token=34b85754-a269-4c8b-9af0-e716fb21163f",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fmid-tier%2Fflip-14.jpg?alt=media&token=dc866aeb-fc5c-4621-9298-516043843d7c"
		]
	},
	{
		label: "Luxury",
		description: "Bespoke + premium. Designer/architect driven. Wide-plank floors, inset cabinetry, panel-ready appliances, quartzite/marble slabs, meticulous millwork, integrated doors, and gallery lighting.",
		featuredImage: "https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fluxury%2Ffeatured-luxury.png?alt=media&token=87c81a66-7af5-4e3e-82de-076022b2f6d0",
		images: [
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fluxury%2Fluxury-01.jpg?alt=media&token=f9f76ae5-09b7-4daf-8383-77a05ee0fbe0",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fluxury%2Fluxury-02.jpg?alt=media&token=f4c30a67-f3a6-49d5-8364-091437e9071d",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fluxury%2Fluxury-03.jpg?alt=media&token=82a84c00-567a-450f-a2bc-d32809b2218a",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fluxury%2Fluxury-04.png?alt=media&token=148cb374-a575-4e45-a521-a58ee3f49e16",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fluxury%2Fluxury-05.jpg?alt=media&token=d299e7f8-71ff-4fa1-83be-4339db0496be",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fluxury%2Fluxury-06.jpg?alt=media&token=40ed3f59-4eed-459c-ae96-0d3bf906990d",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fluxury%2Fluxury-07.jpg?alt=media&token=f5a69ce2-4ef1-4613-95ff-76985b474c56",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fluxury%2Fluxury-08.png?alt=media&token=b5157bd6-3d82-4082-b534-ab67f022e0ad",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fluxury%2Fluxury-09.jpg?alt=media&token=845794b0-f9be-4dad-8a97-63897de90308",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fluxury%2Fluxury-10.jpg?alt=media&token=55f0156d-10c8-467b-8081-d48c95beb97e",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fluxury%2Fluxury-11.jpg?alt=media&token=2feb6cf7-14e7-459b-942f-29447891dd83",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fluxury%2Fluxury-12.jpg?alt=media&token=2d594a94-782b-41cf-b35c-91616fce61c9",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fluxury%2Fluxury-13.jpg?alt=media&token=38ddb469-2a3c-4e12-8e4d-1a9b002b62b3",
			"https://firebasestorage.googleapis.com/v0/b/clozers.firebasestorage.app/o/remodel_levels%2Fluxury%2Fluxury-14.jpg?alt=media&token=8bbd76d7-7ee2-432c-959d-29973f8a03eb"
		]
	}
];

export const defaultAdjustments = [
	{
		label: "Default adjustments",
		all: true,
		adjustments: [
			{ label: "Bedroom", value: "$8k", variants: [] },
			{ label: "Bathroom", value: "$12k", variants: [] },
			{ label: "Half bath", value: "$4k", variants: [] },
			{ label: "Garage", value: null, variants: [
				{ label: "One-car", value: "$8k" },
				{ label: "Two-car", value: "$16k" },
				
			] },
			{ 
				label: "Pool",
				value: "$30k",
				variants: []
			},
			{ label: "Story-count", value: "5% premium for single-story homes.", variants: [] },
			{ label: "ADU", value: "$50k-$100k depending on size, condition, legal status, and rental potential.", variants: [] }
		]
	}
];

export const defaultRawAdjustments = 
`- Bedrooms $8k
- Bathrooms $12k
- Half bath $4k
- Garage:
	- One-car $8k
	- Two-car $16k
- Swimming pool (in-ground) $30k
- Story count 5% premium for single-story homes
- ADU $50k-$100k depending on size, condition, legal status, and rental potential.
`;

export const adjustmentColours = [
	"bg-colour-red-01",
	"bg-colour-pink-01",
	"bg-colour-magenta-01",
	"bg-colour-purple-01",
	"bg-colour-blue-01",
	"bg-colour-aqua-01",
	"bg-colour-jade-01",
	"bg-colour-green-01",
	"bg-colour-yellow-01",
	"bg-colour-orange-01",
	"bg-colour-brown-01",
	"bg-colour-gray-03",
	"bg-colour-gray-02",
	"bg-colour-white"
];

export const conditionOptions = [
	{
		label: "Modern Build",
		value: "modern-build",
		selected: false
	},
	{
		label: "Recently Remodeled",
		value: "recently-remodeled",
		selected: false
	},
	{
		label: "Well-Maintained – Contemporary",
		value: "well-maintained-contemporary",
		selected: false
	},
	{
		label: "Well-Maintained – Traditional",
		value: "well-maintained-traditional",
		selected: false
	},
	{
		label: "Moderate Rehab",
		value: "moderate-rehab",
		selected: false
	},
	{
		label: "Gut Rehab",
		value: "gut-rehab",
		selected: false
	}
];

/**
 * Gentry Repair Cost System
 * Band-based approach: Base cost determined by square footage + condition level
 * Combines with big ticket items (MARICOPA_SFR_REHAB) for total repair cost
 */
export const GENTRY_AZ_REHAB_COST = {
	market: "Gentry, AZ",
	bands: [
		{ min: 1,    max: 1500, light: 15000, medium: 35000, heavy: 45000 },
		{ min: 1501, max: 2500, light: 20000, medium: 50000, heavy: 85000 },
		{ min: 2501, max: 3500, light: 30000, medium: 70000, heavy: 100000 },
		{ min: 3501, max: 5000, light: 40000, medium: 85000, heavy: 125000 },
		{ min: 5001, max: Infinity, light: 55000, medium: 70000, heavy: 160000 }
	]
};

/**
 * Maricopa County, AZ — Big Ticket Items for Repair Cost Calculations
 * Used by Gentry method for flagging and pricing major repair items
 */
export const MARICOPA_SFR_REHAB = {
	market: "Maricopa County, AZ",

	big_ticket_items: {
		electrical_panel: {
			unit: "allowance", low: 2000, high: 4000,
			llm_look_for: "Brand/type on panel (e.g., FPE/Zinsco), scorch marks, double-lugging, missing covers; clues of aluminum/cloth wiring."
		},
		foundation: {
			unit: "allowance", low: 5000, high: 20000,
			llm_look_for: "Stair-step cracks in block/stem walls, wide diagonal cracks over doors/windows, separated baseboards, doors out of square across multiple rooms."
		},
		hvac: {
			unit: "allowance", low: 6000, high: 12000,
			llm_look_for: "Aged/rusted condenser or roof pack unit, damaged fins, patched ducts on roof, old mercury thermostat; nameplate year looks 15+ yrs."
		},
		plumbing_repipe_or_sewer: {
			unit: "allowance", low: 4000, high: 9000,
			llm_look_for: "Corroded/galvanized supply near water heater, repeated ceiling/wall leak stains, slab leak patches; (true sewer scope not visible—flag only)."
		},
		roof: {
			unit: "allowance", low: 8000, high: 20000,
			llm_look_for: "Tile roofs: slipped/missing tiles, exposed underlayment at eaves/valleys (underlayment reset risk). Flat/foam: ponding stains, patched seams; interior ceiling stains."
		},
		windows: {
			unit: "allowance", low: 8000, high: 18000,
			llm_look_for: "Fogged dual panes (condensation between panes), failed seals, single-pane aluminum sliders, rotten sills/trim."
		},
		water_damage_mold: {
			unit: "allowance", low: 2000, high: 10000,
			llm_look_for: "Ceiling/wall stains, bubbling/peeling paint, swollen baseboards/cabinets, dark spotting on drywall—note likely source (roof/plumbing/HVAC)."
		},
		pool_resurface: {
			unit: "allowance", low: 5000, high: 20000,
			llm_look_for: "Plaster delamination/peeling, rebar rust pops, cracked coping/deck."
		}
	}
};

/**
 * Price Per Square Foot Template
 * Template #1: Rehab cost calculated as price per sqft × property square footage
 * Reference square footage of 1,500 is used for display calculations
 */
export const PRICE_PER_SQFT_TEMPLATE = {
	referenceSqft: 1500, // Reference square footage for display calculations
	tiers: [
		{
			id: "fully_renovated",
			name: "Fully Renovated",
			pricePerSqft: 0,
			description: "Property is freshly renovated with modern finishes throughout. No visible wear, damage, or deferred maintenance — move-in ready as-is.",
			timeline: "0 weeks"
		},
		{
			id: "rent_ready",
			name: "Rent-ready",
			pricePerSqft: 10,
			description: "Property is functional and livable but shows its age. Paint touch-ups, minor fixture swaps, basic cleaning. No major systems or finish replacements needed.",
			timeline: "< 1 week"
		},
		{
			id: "partial_rehab",
			name: "Partial rehab",
			pricePerSqft: 25,
			description: "Cosmetic refresh: paint, flooring, fixtures. Either kitchen or bathrooms updated (not both fully). No structural or major mechanical work.",
			timeline: "2-3 weeks"
		},
		{
			id: "full_rehab",
			name: "Full rehab",
			pricePerSqft: 35,
			description: "Full interior remodel: kitchen, bathrooms, flooring, paint, fixtures throughout. No significant exterior cosmetic work beyond minor touch-ups.",
			timeline: "4-6 weeks"
		},
		{
			id: "full_rehab_exterior_cosmetics",
			name: "Full rehab + exterior cosmetics",
			pricePerSqft: 40,
			description: "Full interior remodel plus meaningful exterior work: full repaint, siding/trim repair, or significant landscaping reset.",
			timeline: "6-8 weeks"
		},
		{
			id: "full_rehab_several_big_ticket_items",
			name: "Full rehab + several big ticket items",
			pricePerSqft: 45,
			description: "Full interior remodel plus multiple expensive repairs: roof, HVAC, electrical panel, foundation work, plumbing overhaul, or similar.",
			timeline: "8-10 weeks"
		},
		{
			id: "gut_rehab",
			name: "Gut rehab",
			pricePerSqft: 62,
			description: "Property gutted to studs or near-gut scope. Major demo/rebuild, severe remediation, or distressed/condemned condition. All trades involved.",
			timeline: "10+ weeks"
		}
	]
};

/**
 * Rehab Template — FOUR TIERS ON A FLAT SIZE-BANDED TABLE (2026-08-05)
 *
 *     cost = tierTable[size band][tier]
 *
 * The six-rung `$/sqft × size-decay × sqft` ladder was retired. It had been CARVED to
 * exclude systems (L2 $40 − $10.38 = $30) on the assumption a separate big-ticket
 * allowance would add them back; #1667 severed that allowance and left the carved rungs,
 * so old stock carried zero dollars for a roof. Systems now move the TIER and the tier's
 * price is all-in — one mechanism, nothing to keep in sync.
 *
 * ⚠️ MIRRORED IN functions/defaults/index.js, which is what prices every company that has
 * NOT stored its own template. Change both or they diverge silently.
 *
 * `levels` is RETAINED, unused by the repair pipeline, purely so a company that stored a
 * legacy ladder still round-trips through Settings without its doc being rewritten.
 */
export const REHAB_LEVELS_TEMPLATE = {
	referenceSqft: 1500, // Reference square footage for display calculations
	// `maxSqft` is EXCLUSIVE: a 1,250 sqft house is in the second band.
	// Provenance for every number, and why Turnkey is not $0, lives in
	// functions/repairCostV2/rentReadyLevel.js.
	tierTable: [
		{ maxSqft: 1250, turnkey: 7000, light: 18000, medium: 35000, heavy: 65000 },
		{ maxSqft: 1750, turnkey: 8000, light: 22000, medium: 42000, heavy: 80000 },
		{ maxSqft: 2500, turnkey: 9500, light: 28000, medium: 52000, heavy: 100000 },
		{ maxSqft: null, turnkey: 11000, light: 35000, medium: 65000, heavy: 125000 }
	],
	// Legacy — retained for back-compat only. Not a pricing input.
	levels: [
		{
			id: "level_0",
			level: 0,
			name: "Pristine",
			pricePerSqft: 4,
			description: "Move-in ready. Kitchen, baths, floors and walls are all modern or recently redone, with no damage and nothing missing. This rate is ALL-IN: roof, HVAC, water heater, electrical panel and plumbing are included in it, not charged separately.",
			timeline: null
		},
		{
			id: "level_1",
			level: 1,
			name: "Lipstick",
			pricePerSqft: 15,
			description: "Everything works and nothing needs replacing — it just looks tired. Paint, flooring, light fixtures, mirrors and landscaping. This rate is ALL-IN: roof, HVAC, water heater, electrical panel and plumbing are included in it, not charged separately.",
			timeline: "1-2 weeks"
		},
		{
			id: "level_2",
			level: 2,
			name: "Cosmetic Update",
			pricePerSqft: 15,
			description: "ONE wet room needs redoing — either the kitchen or the bathrooms, not both — plus paint and flooring throughout. No damage, no walls moved, no structural work. This rate is ALL-IN: roof, HVAC, water heater, electrical panel and plumbing are included in it, not charged separately.",
			timeline: "3-4 weeks"
		},
		{
			id: "level_3",
			level: 3,
			name: "Moderate Overhaul",
			pricePerSqft: 30,
			description: "BOTH the kitchen and the bathrooms need redoing, or either one has components missing or destroyed. Paint and flooring throughout. Still no structural damage. This rate is ALL-IN: roof, HVAC, water heater, electrical panel and plumbing are included in it, not charged separately.",
			timeline: "6-8 weeks"
		},
		{
			id: "level_4",
			level: 4,
			name: "Full Renovation",
			pricePerSqft: 45,
			description: "Damage or demolition across multiple rooms — exposed studs, missing drywall, water damage, structural cracking or bowing in the foundation, or both wet rooms destroyed. Every trade is involved. This rate is ALL-IN: roof, HVAC, water heater, electrical panel and plumbing are included in it, not charged separately.",
			timeline: "8+ weeks"
		},
		{
			id: "level_5",
			level: 5,
			name: "Complete Rebuild",
			pricePerSqft: 100,
			description: "Stripped to the framing throughout, or fire or collapse damage. (Foundation cracking and standing water sit at Level 4 — serious, but not a rebuild.) This rate is ALL-IN: roof, HVAC, water heater, electrical panel and plumbing are included in it, not charged separately.",
			timeline: "Case by case"
		}
	]
};